import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  serverTimestamp,
  onSnapshot,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch
} from 'firebase/firestore';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { currentUser, updateCurrentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Impersonation state
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  
  // Get current effective user (either impersonated or real)
  const effectiveUser = impersonatedUser || currentUser;
  
  // State for all data
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]); // חדש - הערות
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    // Wait until we have a complete user object with role
    if (!effectiveUser?.id || !effectiveUser?.role) {
      return;
    }

    const projectsQueryRef = query(
      collection(db, COLLECTIONS.PROJECTS),
      orderBy('createdAt', 'desc')
    );

    const tasksQueryRef = query(
      collection(db, COLLECTIONS.TASKS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeProjects = onSnapshot(projectsQueryRef, (snapshot) => {
      const projectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(projectsData);
    }, (error) => {
      setProjects([]);
    });

    const unsubscribeTasks = onSnapshot(tasksQueryRef, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTasks(tasksData);
    }, (error) => {
      setTasks([]);
    });

    // Load users
    const usersQueryRef = collection(db, COLLECTIONS.USERS);
    const unsubscribeUsers = onSnapshot(usersQueryRef, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
    }, (error) => {
      setUsers([]);
    });

    // Load departments
    const departmentsQueryRef = collection(db, COLLECTIONS.DEPARTMENTS);
    const unsubscribeDepartments = onSnapshot(departmentsQueryRef, (snapshot) => {
      const departmentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDepartments(departmentsData);
    }, (error) => {
      setDepartments([]);
    });

    // Load teams
    const teamsQueryRef = collection(db, COLLECTIONS.TEAMS);
    const unsubscribeTeams = onSnapshot(teamsQueryRef, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTeams(teamsData);
    }, (error) => {
      setTeams([]);
    });

    // Load comments
    const commentsQueryRef = query(
      collection(db, COLLECTIONS.COMMENTS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeComments = onSnapshot(commentsQueryRef, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setComments(commentsData);
    }, (error) => {
      console.error('Error loading comments:', error);
      setComments([]);
    });
// Load suggestions
    const suggestionsQueryRef = query(
      collection(db, 'suggestions'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeSuggestions = onSnapshot(suggestionsQueryRef, (snapshot) => {
      const suggestionsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSuggestions(suggestionsData);
    }, (error) => {
      console.error('Error loading suggestions:', error);
      setSuggestions([]);
    });
    setLoading(false);

    return () => {
      if (typeof unsubscribeProjects === 'function') unsubscribeProjects();
      if (typeof unsubscribeTasks === 'function') unsubscribeTasks();
      if (typeof unsubscribeUsers === 'function') unsubscribeUsers();
      if (typeof unsubscribeDepartments === 'function') unsubscribeDepartments();
      if (typeof unsubscribeTeams === 'function') unsubscribeTeams();
      if (typeof unsubscribeComments === 'function') unsubscribeComments();
      if (typeof unsubscribeSuggestions === 'function') unsubscribeSuggestions();
    };
  }, [effectiveUser?.id]);


  // Remove demo initialization: departments and users should be loaded from Firestore elsewhere
  const initializeDemoData = () => {};

  // Task operations
  const createTask = async (taskData) => {
    try {
      // Determine if task requires approval
      const requiresApproval = taskData.scope === 'managerial' || 
                              (taskData.assigneeId !== effectiveUser.id && taskData.scope !== 'personal');
      
      // Set initial status based on approval requirement
      let initialStatus = taskData.status || 'ממתין לעבודה';
      if (requiresApproval && !taskData.status) {
        initialStatus = 'ממתין לעבודה';
      }
      
      const newTask = {
        ...taskData,
        creatorId: effectiveUser.id,
        createdAt: serverTimestamp(),
        acknowledged: false,
        requiresApproval: requiresApproval,
        status: initialStatus,
        approvedBy: null,
        approvedAt: null
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.TASKS), newTask);

      // Auto-add assignees to project team if projectId exists
      try {
        if (newTask.projectId) {
          const project = projects.find(p => p.id === newTask.projectId);
          const currentTeam = Array.isArray(project?.teamMembers) ? project.teamMembers : [];
          const assignees = Array.isArray(newTask.assigneeIds) && newTask.assigneeIds.length
            ? newTask.assigneeIds
            : (newTask.assigneeId ? [newTask.assigneeId] : []);
          const updatedTeam = Array.from(new Set([...currentTeam, ...assignees]));
          if (project && updatedTeam.length !== currentTeam.length) {
            await updateDoc(doc(db, COLLECTIONS.PROJECTS, project.id), {
              teamMembers: updatedTeam,
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (teamErr) {
        // Could not auto-add assignees to project team
      }

      return docRef.id;
    } catch (error) {
      // Fallback to local state for demo
      const localTask = {
        id: `task_${Date.now()}`,
        ...taskData,
        creatorId: effectiveUser.id,
        createdAt: new Date(),
        acknowledged: false,
        status: taskData.status || 'במסלול'
      };
      
      setTasks(prev => [localTask, ...prev]);

      
      return localTask.id;
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      // Try to update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Auto-add assignees to project team if projectId exists
      try {
        const taskAfter = tasks.find(t => t.id === taskId) || {};
        const projectId = updates.projectId || taskAfter.projectId;
        if (projectId) {
          const project = projects.find(p => p.id === projectId);
          const currentTeam = Array.isArray(project?.teamMembers) ? project.teamMembers : [];
          const assignees = Array.isArray(updates.assigneeIds) && updates.assigneeIds.length
            ? updates.assigneeIds
            : (updates.assigneeId ? [updates.assigneeId] : (Array.isArray(taskAfter.assigneeIds) ? taskAfter.assigneeIds : (taskAfter.assigneeId ? [taskAfter.assigneeId] : [])));
          const updatedTeam = Array.from(new Set([...currentTeam, ...assignees]));
          if (project && updatedTeam.length !== currentTeam.length) {
            await updateDoc(doc(db, COLLECTIONS.PROJECTS, project.id), {
              teamMembers: updatedTeam,
              updatedAt: serverTimestamp()
            });
          }
        }
      } catch (teamErr) {
        // Could not auto-add assignees to project team on update
      }
      
    } catch (error) {
      // Fallback to local state update
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { ...task, ...updates, updatedAt: new Date() }
            : task
        )
      );
    }
  };

  const acknowledgeTask = async (taskId) => {
    try {
      const acknowledgeData = {
        acknowledged: true,
        acknowledgedAt: serverTimestamp()
      };

      // Try to update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), acknowledgeData);
      
    } catch (error) {
      // Fallback to local state update
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { ...task, acknowledged: true, acknowledgedAt: new Date() }
            : task
        )
      );
    }
  };

  // New function for approving tasks
  const approveTask = async (taskId) => {
    try {
      const approvalData = {
        status: 'במסלול',
        approvedBy: effectiveUser.id,
        approvedAt: serverTimestamp(),
        acknowledged: true,
        acknowledgedAt: serverTimestamp()
      };

      // Try to update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TASKS, taskId), approvalData);
      
    } catch (error) {
      // Fallback to local state update
      setTasks(prev => 
        prev.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                status: 'במסלול',
                approvedBy: effectiveUser.id,
                approvedAt: new Date(),
                acknowledged: true,
                acknowledgedAt: new Date()
              }
            : task
        )
      );
    }
  };

  // Project operations
  const createProject = async (projectData) => {
    try {
      const newProject = {
        ...projectData,
        createdBy: effectiveUser.id,
        createdAt: serverTimestamp(),
        status: projectData.status || 'במסלול',
        progress: projectData.progress || 0
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.PROJECTS), newProject);
      
      return docRef.id;
    } catch (error) {
      // Fallback to local state for demo
      const localProject = {
        id: `project_${Date.now()}`,
        ...projectData,
        createdBy: effectiveUser.id,
        createdAt: new Date(),
        status: projectData.status || 'במסלול',
        progress: projectData.progress || 0
      };
      
      setProjects(prev => [localProject, ...prev]);
      
      return localProject.id;
    }
  };

  // Team operations
  const createTeam = async (teamData) => {
    try {
      const newTeam = {
        ...teamData,
        createdBy: effectiveUser.id,
        createdAt: serverTimestamp(),
        isActive: teamData.isActive ?? true
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.TEAMS), newTeam);
      
      // עדכון המשתמשים עם ה-teamId החדש
      const batch = writeBatch(db);
      
      // עדכון מנהל הצוות עם ה-departmentId
      if (teamData.managerId) {
        const managerRef = doc(db, COLLECTIONS.USERS, teamData.managerId);
        batch.update(managerRef, { 
          departmentId: teamData.departmentId,
          teamId: docRef.id,
          updatedAt: serverTimestamp()
        });
      }
      
      // עדכון חברי הצוות עם ה-teamId החדש
      if (teamData.teamMembers && teamData.teamMembers.length > 0) {
        teamData.teamMembers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamId: docRef.id,
            departmentId: teamData.departmentId,
            updatedAt: serverTimestamp()
          });
        });
      }
      
      await batch.commit();
      
      // עדכון המצב המקומי
      setUsers(prev => 
        prev.map(user => {
          if (user.id === teamData.managerId) {
            return { ...user, departmentId: teamData.departmentId, teamId: docRef.id, updatedAt: new Date() };
          }
          if (teamData.teamMembers && teamData.teamMembers.includes(user.id)) {
            return { ...user, teamId: docRef.id, departmentId: teamData.departmentId, updatedAt: new Date() };
          }
          return user;
        })
      );
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating team:', error);
      
      // Fallback to local state only
      const localTeam = {
        id: `team_${Date.now()}`,
        ...teamData,
        createdBy: effectiveUser.id,
        createdAt: new Date(),
        isActive: teamData.isActive ?? true
      };
      setTeams(prev => [localTeam, ...prev]);
      
      // Update local users state
      setUsers(prev => 
        prev.map(user => {
          if (user.id === teamData.managerId) {
            return { ...user, departmentId: teamData.departmentId, teamId: localTeam.id, updatedAt: new Date() };
          }
          if (teamData.teamMembers && teamData.teamMembers.includes(user.id)) {
            return { ...user, teamId: localTeam.id, departmentId: teamData.departmentId, updatedAt: new Date() };
          }
          return user;
        })
      );
      
      return localTeam.id;
    }
  };

  const updateTeam = async (teamId, updates) => {
    try {
      // Get current team to compare teamMembers
      const currentTeam = teams.find(t => t.id === teamId);
      const oldTeamMembers = currentTeam?.teamMembers || [];
      const newTeamMembers = updates.teamMembers || [];
      
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.TEAMS, teamId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Handle teamMembers changes
      if (updates.teamMembers !== undefined) {
        const batch = writeBatch(db);
        
        // Remove teamId from users no longer in teamMembers
        const removedUsers = oldTeamMembers.filter(id => !newTeamMembers.includes(id));
        removedUsers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamId: null,
            updatedAt: serverTimestamp()
          });
        });
        
        // Add teamId to new teamMembers
        const addedUsers = newTeamMembers.filter(id => !oldTeamMembers.includes(id));
        addedUsers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamId: teamId,
            departmentId: currentTeam.departmentId,
            updatedAt: serverTimestamp()
          });
        });
        
        if (removedUsers.length > 0 || addedUsers.length > 0) {
          await batch.commit();
          
          // Update local users state
          setUsers(prev => 
            prev.map(user => {
              if (removedUsers.includes(user.id)) {
                return { ...user, teamId: null, updatedAt: new Date() };
              }
              if (addedUsers.includes(user.id)) {
                return { ...user, teamId: teamId, departmentId: currentTeam.departmentId, updatedAt: new Date() };
              }
              return user;
            })
          );
        }
      }
      
      // Also update local state immediately
      setTeams(prev => 
        prev.map(team => 
          team.id === teamId 
            ? { ...team, ...updates, updatedAt: new Date() }
            : team
        )
      );
      
    } catch (error) {
      console.error('Error updating team:', error);
      
      // Fallback to local state update only
      setTeams(prev => 
        prev.map(team => 
          team.id === teamId 
            ? { ...team, ...updates, updatedAt: new Date() }
            : team
        )
      );
    }
  };

  const deleteTeam = async (teamId) => {
    try {
      // Get current team to find teamMembers
      const currentTeam = teams.find(t => t.id === teamId);
      const teamMembers = currentTeam?.teamMembers || [];
      
      // Remove teamId from all team members
      if (teamMembers.length > 0) {
        const batch = writeBatch(db);
        
        teamMembers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            teamId: null,
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        // Update local users state
        setUsers(prev => 
          prev.map(user => 
            teamMembers.includes(user.id)
              ? { ...user, teamId: null, updatedAt: new Date() }
              : user
          )
        );
      }
      
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.TEAMS, teamId));
      
      // Also update local state immediately
      setTeams(prev => prev.filter(team => team.id !== teamId));
      
    } catch (error) {
      console.error('Error deleting team:', error);
      
      // Fallback to local state update only
      setTeams(prev => prev.filter(team => team.id !== teamId));
    }
  };
// Suggestion operations
  const createSuggestion = async (suggestionData) => {
    try {
      const newSuggestion = {
        ...suggestionData,
        createdAt: serverTimestamp(),
        status: 'pending'
      };
      
      const docRef = await addDoc(collection(db, 'suggestions'), newSuggestion);
      return docRef.id;
    } catch (error) {
      console.error('Error creating suggestion:', error);
      // Fallback to local state
      const localSuggestion = {
        id: `suggestion_${Date.now()}`,
        ...suggestionData,
        createdAt: new Date()
      };
      setSuggestions(prev => [localSuggestion, ...prev]);
      return localSuggestion.id;
    }
  };

  const updateSuggestion = async (suggestionId, updates) => {
    try {
      await updateDoc(doc(db, 'suggestions', suggestionId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating suggestion:', error);
      setSuggestions(prev => 
        prev.map(s => s.id === suggestionId 
          ? { ...s, ...updates, updatedAt: new Date() }
          : s
      )
      );
    }
  };

  const deleteSuggestion = async (suggestionId) => {
    try {
      await deleteDoc(doc(db, 'suggestions', suggestionId));
    } catch (error) {
      console.error('Error deleting suggestion:', error);
    }
    setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
  };
  // Department operations
  const createDepartment = async (departmentData) => {
    try {
      const newDepartment = {
        ...departmentData,
        createdBy: effectiveUser.id,
        createdAt: serverTimestamp(),
        isActive: departmentData.isActive ?? true
      };
      const docRef = await addDoc(collection(db, COLLECTIONS.DEPARTMENTS), newDepartment);
      
      // עדכון המשתמשים עם ה-departmentId החדש
      if (departmentData.teamMembers && departmentData.teamMembers.length > 0) {
        const batch = writeBatch(db);
        
        departmentData.teamMembers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentId: docRef.id,
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        // עדכון המצב המקומי
        setUsers(prev => 
          prev.map(user => 
            departmentData.teamMembers.includes(user.id)
              ? { ...user, departmentId: docRef.id, updatedAt: new Date() }
              : user
          )
        );
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating department:', error);
      
      // Fallback to local state only
      const localDept = {
        id: `dept_${Date.now()}`,
        ...departmentData,
        createdBy: effectiveUser.id,
        createdAt: new Date(),
        isActive: departmentData.isActive ?? true
      };
      setDepartments(prev => [localDept, ...prev]);
      
      // Update local users state
      if (departmentData.teamMembers && departmentData.teamMembers.length > 0) {
        setUsers(prev => 
          prev.map(user => 
            departmentData.teamMembers.includes(user.id)
              ? { ...user, departmentId: localDept.id, updatedAt: new Date() }
              : user
          )
        );
      }
      
      return localDept.id;
    }
  };

  const updateProject = async (projectId, updates) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.PROJECTS, projectId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Also update local state immediately
      setProjects(prev => 
        prev.map(project => 
          project.id === projectId 
            ? { ...project, ...updates, updatedAt: new Date() }
            : project
        )
      );
      
    } catch (error) {
      // Fallback to local state update only
      setProjects(prev => 
        prev.map(project => 
          project.id === projectId 
            ? { ...project, ...updates, updatedAt: new Date() }
            : project
        )
      );
    }
  };

  const deleteProject = async (projectId) => {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.PROJECTS, projectId));
      
      // Also update local state immediately
      setProjects(prev => prev.filter(project => project.id !== projectId));
      
    } catch (error) {
      // Fallback to local state update only
      setProjects(prev => prev.filter(project => project.id !== projectId));
    }
  };

  const deleteTask = async (taskId) => {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.TASKS, taskId));
      
      // Also update local state immediately
      setTasks(prev => prev.filter(task => task.id !== taskId));
      
    } catch (error) {
      // Fallback to local state update only
      setTasks(prev => prev.filter(task => task.id !== taskId));
    }
  };

  // User operations
  const updateUser = async (userId, updates) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Force refresh users data from Firestore
      const usersQueryRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(usersQueryRef);
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersData);
      
      // If updating current user, also update AuthContext
      if (userId === currentUser?.id) {
        updateCurrentUser(updates);
      }
      
    } catch (error) {
      // Fallback to local state update only
      setUsers(prev => 
        prev.map(user => 
          user.id === userId 
            ? { ...user, ...updates, updatedAt: new Date() }
            : user
        )
      );
      
      // If updating current user, also update AuthContext
      if (userId === currentUser?.id) {
        updateCurrentUser(updates);
      }
    }
  };

  // Department operations
  const updateDepartment = async (departmentId, updates) => {
    try {
      // Get current department to compare teamMembers
      const currentDept = departments.find(d => d.id === departmentId);
      const oldTeamMembers = currentDept?.teamMembers || [];
      const newTeamMembers = updates.teamMembers || [];
      
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.DEPARTMENTS, departmentId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Handle teamMembers changes
      if (updates.teamMembers !== undefined) {
        const batch = writeBatch(db);
        
        // Remove departmentId from users no longer in teamMembers
        const removedUsers = oldTeamMembers.filter(id => !newTeamMembers.includes(id));
        removedUsers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentId: null,
            updatedAt: serverTimestamp()
          });
        });
        
        // Add departmentId to new teamMembers
        const addedUsers = newTeamMembers.filter(id => !oldTeamMembers.includes(id));
        addedUsers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentId: departmentId,
            updatedAt: serverTimestamp()
          });
        });
        
        if (removedUsers.length > 0 || addedUsers.length > 0) {
          await batch.commit();
          
          // Update local users state
          setUsers(prev => 
            prev.map(user => {
              if (removedUsers.includes(user.id)) {
                return { ...user, departmentId: null, updatedAt: new Date() };
              }
              if (addedUsers.includes(user.id)) {
                return { ...user, departmentId: departmentId, updatedAt: new Date() };
              }
              return user;
            })
          );
        }
      }
      
      // Also update local state immediately
      setDepartments(prev => 
        prev.map(dept => 
          dept.id === departmentId 
            ? { ...dept, ...updates, updatedAt: new Date() }
            : dept
        )
      );
      
    } catch (error) {
      console.error('Error updating department:', error);
      
      // Fallback to local state update only
      setDepartments(prev => 
        prev.map(dept => 
          dept.id === departmentId 
            ? { ...dept, ...updates, updatedAt: new Date() }
            : dept
        )
      );
    }
  };

  const deleteDepartment = async (departmentId) => {
    try {
      // Get current department to find teamMembers
      const currentDept = departments.find(d => d.id === departmentId);
      const teamMembers = currentDept?.teamMembers || [];
      
      // Remove departmentId from all team members
      if (teamMembers.length > 0) {
        const batch = writeBatch(db);
        
        teamMembers.forEach(userId => {
          const userRef = doc(db, COLLECTIONS.USERS, userId);
          batch.update(userRef, { 
            departmentId: null,
            updatedAt: serverTimestamp()
          });
        });
        
        await batch.commit();
        
        // Update local users state
        setUsers(prev => 
          prev.map(user => 
            teamMembers.includes(user.id)
              ? { ...user, departmentId: null, updatedAt: new Date() }
              : user
          )
        );
      }
      
      // Delete from Firestore
      await deleteDoc(doc(db, COLLECTIONS.DEPARTMENTS, departmentId));
      
      // Also update local state immediately
      setDepartments(prev => prev.filter(dept => dept.id !== departmentId));
      
    } catch (error) {
      console.error('Error deleting department:', error);
      
      // Fallback to local state update only
      setDepartments(prev => prev.filter(dept => dept.id !== departmentId));
    }
  };

  // Comment operations
  const createComment = async (commentData) => {
    try {
      const newComment = {
        ...commentData,
        userId: effectiveUser.id,
        userName: effectiveUser.name || effectiveUser.email,
        createdAt: serverTimestamp(),
        isRead: false
      };
  
      // Save to Firestore
      const docRef = await addDoc(collection(db, COLLECTIONS.COMMENTS), newComment);
      
      // Get the task
      const task = tasks.find(t => t.id === commentData.taskId);
      if (!task) return docRef.id;
      
      // Create notifications for all relevant users
      const notifyUsers = new Set();
      
      // Add task creator
      if (task.creatorId && task.creatorId !== effectiveUser.id) {
        notifyUsers.add(task.creatorId);
      }
      
      // Add assignee(s)
      if (task.assigneeId && task.assigneeId !== effectiveUser.id) {
        notifyUsers.add(task.assigneeId);
      }
      
      // Add all assignees if array exists
      if (Array.isArray(task.assigneeIds)) {
        task.assigneeIds.forEach(id => {
          if (id !== effectiveUser.id) {
            notifyUsers.add(id);
          }
        });
      }
      
      // Create notification for each relevant user
      for (const notifyUserId of notifyUsers) {
        await createNotification({
          userId: notifyUserId,
          type: 'comment',
          title: 'הערה חדשה במשימה',
          message: `${effectiveUser.name || effectiveUser.email} הוסיף/ה הערה במשימה "${task.title}"`,
          taskId: task.id,
          commentId: docRef.id,
          fromUserId: effectiveUser.id
        });
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  };

  const getTaskComments = (taskId) => {
    return comments.filter(comment => comment.taskId === taskId);
  };

  const getUnreadCommentsForUser = (userId) => {
    // Get all tasks where:
    // 1. User created the task OR
    // 2. User is assigned to the task
    const relevantTasks = tasks.filter(task => 
      task.creatorId === userId || 
      task.assigneeId === userId ||
      (Array.isArray(task.assigneeIds) && task.assigneeIds.includes(userId))
    );
    
    const taskIds = relevantTasks.map(task => task.id);
    
    // Get all comments on those tasks that:
    // 1. Are not by the current user
    // 2. Are unread
    return comments.filter(comment => 
      taskIds.includes(comment.taskId) && 
      comment.userId !== userId &&
      !comment.isRead
    );
  };

  const markCommentAsRead = async (commentId) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.COMMENTS, commentId), {
        isRead: true,
        readAt: serverTimestamp()
      });
      
      // Update local state immediately for instant UI feedback
      setComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, isRead: true, readAt: new Date() }
            : comment
        )
      );
      
      console.log(`Comment ${commentId} marked as read`);
    } catch (error) {
      console.error('Error marking comment as read:', error);
    }
  };



  // Notification operations
  const createNotification = async (notificationData) => {
    try {
      const newNotification = {
        id: `notification_${Date.now()}`,
        ...notificationData,
        createdAt: new Date(),
        isRead: false
      };

      // Add to local state immediately for demo
      setNotifications(prev => [newNotification, ...prev]);
      
      return newNotification.id;
    } catch (error) {
      throw error;
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true, readAt: new Date() }
            : notification
        )
      );
    } catch (error) {
      throw error;
    }
  };

  const value = {
    // Data
    departments,
    teams,
    projects,
    tasks,
    templates,
    notifications,
    users,
    comments,
    suggestions,
    
    // Task operations
    createTask,
    updateTask,
    acknowledgeTask,
    approveTask,
    deleteTask,
    
    // Project operations
    createProject,
    updateProject,
    deleteProject,

    // Team operations
    createTeam,
    updateTeam,
    deleteTeam,

    // Department operations
    createDepartment,
    updateDepartment,
    deleteDepartment,
    
    // User operations
    updateUser,
    
    // Comment operations
    createComment,
    getTaskComments,
    getUnreadCommentsForUser,
    markCommentAsRead,
    
    // Notification operations
    createNotification,
    markNotificationAsRead,
    
    // Impersonation operations
    impersonatedUser,
    setImpersonatedUser,
    effectiveUser,
    // Suggestion operations
    createSuggestion,
    updateSuggestion,
    deleteSuggestion,
    // Utilities
    loading,
    
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};